/**
 * BusinessSubscriptionPage — post-trial subscription flow (web-only).
 *
 * Users reach this page from the mobile app CTA "Continuar suscripción",
 * which opens https://bookvia.com/business/subscription in an EXTERNAL
 * browser. Payment happens outside the native shell so Apple/Google
 * don't take a cut of the monthly fee.
 *
 * The page authenticates via the standard token, hits the existing
 * `/api/businesses/me/subscribe` endpoint to create a Stripe Checkout
 * session, then redirects the browser to Stripe.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, CreditCard, Shield, Zap, Clock } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { businessesAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function BusinessSubscriptionPage() {
  const { language } = useI18n();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || user?.role !== 'business') {
      navigate('/login?redirect=/business/subscription');
      return;
    }
    businessesAPI
      .getDashboard()
      .then((r) => setPlanInfo(r.data?.plan))
      .catch(() => {});
  }, [authLoading, isAuthenticated, user, navigate]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const res = await businessesAPI.createSubscription(window.location.origin);
        const url = res?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (err) {
      toast.error(
        language === 'es'
          ? 'No pudimos abrir el checkout. Intenta de nuevo.'
          : 'Could not open checkout. Try again.'
      );
      setLoading(false);
    }
  };

  const price = planInfo?.monthly_price_mxn ?? 49.99;
  const state = planInfo?.state;
  const daysLeft = planInfo?.trial_days_left ?? 0;

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="business-subscription-page">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 mb-3">
            <span className="text-2xl font-heading font-extrabold">
              Book<span className="text-[#F05D5E]">via</span>
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold mb-2">
            {state === 'expired'
              ? (language === 'es' ? 'Activa tu suscripción' : 'Activate your subscription')
              : (language === 'es' ? 'Continúa con Bookvia' : 'Continue with Bookvia')}
          </h1>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            {state === 'expired'
              ? (language === 'es'
                ? 'Tu prueba de 30 días terminó. Agrega tu tarjeta para seguir recibiendo reservas.'
                : 'Your 30-day trial ended. Add your card to keep receiving bookings.')
              : (language === 'es'
                ? `Te quedan ${daysLeft} días de prueba. Activa tu suscripción para no perder acceso.`
                : `${daysLeft} trial days remaining. Activate to avoid losing access.`)}
          </p>
        </div>

        <Card className="border-2 border-[#F05D5E]/20 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-[#F05D5E]/10 mx-auto mb-2">
              <Zap className="h-6 w-6 text-[#F05D5E]" />
            </div>
            <CardTitle className="text-lg">
              {language === 'es' ? 'Plan mensual Bookvia' : 'Bookvia monthly plan'}
            </CardTitle>
            <div className="mt-3">
              <span className="text-4xl font-heading font-extrabold">${price}</span>
              <span className="text-muted-foreground text-sm ml-1">
                {language === 'es' ? 'MXN al mes' : 'MXN / month'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {language === 'es'
                ? 'Se cobrará automáticamente cada mes. Cancela cuando quieras.'
                : 'Auto-charged monthly. Cancel anytime.'}
            </p>
          </CardHeader>

          <CardContent className="space-y-4">
            <ul className="space-y-2.5">
              {[
                {
                  es: 'Reservas ilimitadas 24/7',
                  en: 'Unlimited 24/7 bookings',
                },
                {
                  es: 'Cobro automático de anticipos',
                  en: 'Automatic deposit charges',
                },
                {
                  es: 'Panel de estadísticas completo',
                  en: 'Full analytics dashboard',
                },
                {
                  es: 'Recordatorios y notificaciones push',
                  en: 'Reminders and push notifications',
                },
                {
                  es: 'Soporte prioritario por WhatsApp',
                  en: 'Priority WhatsApp support',
                },
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                  <span>{language === 'es' ? b.es : b.en}</span>
                </li>
              ))}
            </ul>

            <Button
              className="w-full h-12 bg-[#F05D5E] hover:bg-[#e04d4e] text-white text-base font-semibold"
              onClick={handleSubscribe}
              disabled={loading}
              data-testid="subscribe-button"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {loading
                ? (language === 'es' ? 'Abriendo pago seguro...' : 'Opening secure checkout...')
                : (language === 'es' ? 'Agregar tarjeta y activar' : 'Add card & activate')}
            </Button>

            <div className="flex items-start gap-2 text-[11px] text-muted-foreground pt-2">
              <Shield className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <p className="leading-snug">
                {language === 'es'
                  ? 'Procesado por Stripe. Bookvia nunca almacena tu tarjeta. Cancela desde tu panel cuando quieras.'
                  : 'Processed by Stripe. Bookvia never stores your card. Cancel anytime from your dashboard.'}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/business/dashboard')}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-to-dashboard"
          >
            <Clock className="h-3.5 w-3.5 inline mr-1" />
            {language === 'es' ? 'Volver a mi panel' : 'Back to my dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
}
